import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import { ContentType, LessonMode } from '../../../Constants';

import DrawFloatingToolbar from '../tools/DrawFloatingToolbar';
import Auth from '../../services/AuthService';
import DrawService from '../../services/DrawService';
import PlayerService from '../../services/PlayerService';

import RichTextEditor, { ADVANCED_CKEDITOR_TOOLBAR, ADVANCED_MATH_CKEDITOR_TOOLBAR, MATH_TOOLBAR, MATH_WITH_TABLE_TOOLBAR, MINIMUM_TOOLBAR } from '../RichTextEditor';

import HtmlComponent from '../HtmlComponent';

import '../../../css/Draw.scss';
import useStyleEvents from '../../../hooks/useStyleEvents';
import UtilsService from '../../services/UtilsService';
import lessonService from '../../services/LessonService';
import responseService from '../../services/ResponseService';
import styleService from '../../services/StyleService';
import navMenuService from '../../services/NavMenuService';
import { register } from '../../../i18n';
import PrintQuestionNumber from './PrintQuestionNumber';
import ShowPreviousResponseButton from '../ShowPreviousResponseButton';
import SubmissionWarningDialog from '../SubmissionWarningDialog';
import ShowPreviousResponseConfirmDialog from '../ShowPreviousResponseConfirmDialog';
import PreviewUnavailableDialog from '../PreviewUnavailableDialog';

const t = register('GlobalQuestionLabels');

const TEXT_RESPONSE_MIN_HEIGHT = 120;
const TEXT_RESPONSE_MAX_HEIGHT = 800;
const TEXT_RESPONSE_MIN_WIDTH = 100;
const TEXT_RESPONSE_MAX_WIDTH = 995;
const TEXT_RESPONSE_MAX_WIDTH_MULTIPART = 790;
const TEXT_RESPONSE_MAX_WIDTH_ACTIVITY = 690;
const TEXT_RESPONSE_RESIZE_STEP = 20;
const TEXT_RESPONSE_RESIZE_STEP_LARGE = 50;

const Draw = observer(({ lessonElementId }) => {
  const {
    drawManager,
    lessonManager,
    responseManager,
  } = useContext(MobXProviderContext);

  const [showSubmissionWarning, setShowSubmissionWarning] = useState(false);
  const [showPreviousResponseConfirm, setShowPreviousResponseConfirm] = useState(false);
  const [showPreviewUnavailable, setShowPreviewUnavailable] = useState(false);
  const [textResponseWidth, setTextResponseWidth] = useState(null);
  const [textResponseHeight, setTextResponseHeight] = useState(TEXT_RESPONSE_MIN_HEIGHT);
  const drawAnchorRef = useRef(null);
  const drawDrawableRef = useRef(null);

  const model = lessonManager.getSlideModel(lessonElementId);
  const drawService = [];
  drawService[lessonElementId] = useMemo(() => new DrawService(model), [lessonElementId]); // eslint-disable-line react-hooks/exhaustive-deps
  drawManager.init(lessonElementId);

  const [rerender, setRerender] = useState(false);

  const imageLoaded = (e) => {
    const { clientHeight } = e.target;
    const bodyHeight = drawService[lessonElementId].getHeight();

    const finalHeight = ((bodyHeight - clientHeight) / 2);
    e.target.style.marginTop = (lessonManager.playerMode === LessonMode.PRINT_PREVIEW) ? '0' : `${finalHeight}px`;
  };

  useStyleEvents(lessonElementId);

  // Get lesson element state early so it can be used in useEffect
  const lessonElementState = responseManager.getLessonElementState(lessonElementId);
  const readOnly = lessonElementState ? (lessonElementState.readOnly || lessonManager.isReadOnly) : false;


  useEffect(() => {
    const init = async () => {
      // remove all text containers leftover from other Draw slides
      // await drawManager.initializeTextContainers([], lessonElementId);

      await drawService[lessonElementId].initialize();
      await drawService[lessonElementId].runtimeObjectReady();

      await drawService[lessonElementId].showResponse();
      // possible timing issue with text containers from other Draw slides showing - rerender may fix it
      setRerender(!rerender);

      if (!UtilsService.isNullOrEmpty(model.imageSource) && UtilsService.isNullOrEmpty(model.altText) && !model.altTextLoaded && UtilsService.isNullOrEmpty(altText)) {
        const contentItemId = model.contentItemId || lessonService.contentItemIdFromResourceFileName(model.imageSource);
        if (!contentItemId) {
          model.altTextLoaded = true;
        } else {
          const contentItem = await lessonService.fetchContentItem(contentItemId);
          if (contentItem && contentItem.altText) {
            setAltText(contentItem.altText);
            model.altText = contentItem.altText;
            model.altTextLoaded = true;
          } else {
            if (!altText) {
              if (imageTitle) {
                setAltText(UtilsService.stripTagsAndEntities(model.imageTitle));
                model.altText = UtilsService.stripTagsAndEntities(model.imageTitle);
                model.altTextLoaded = true;
              }
            }
          }
        }
      }
    };
    if (lessonManager.playerMode !== LessonMode.PRINT_PREVIEW) {
      init();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch for response changes and re-render the canvas
  useEffect(() => {
    if (lessonManager.playerMode === LessonMode.PRINT_PREVIEW) {
      return;
    }
    if (lessonElementState && lessonElementState.currentResponseAnswer && drawService[lessonElementId]) {
      // Small delay to ensure the response data is fully updated
      const timer = setTimeout(() => {
        drawService[lessonElementId].showResponse();
        setRerender(!rerender);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [lessonElementState?.currentResponseAnswer, lessonElementId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch for read-only state changes and deactivate drawing mode when read-only
  useEffect(() => {
    if (drawService[lessonElementId] && readOnly) {
      // Deactivate drawing mode when question becomes read-only
      drawService[lessonElementId].setDrawingMode(false);
      // Deselect any active tools
      drawService[lessonElementId].deselectToolbarButtons();
    }
  }, [readOnly, lessonElementId]); // eslint-disable-line react-hooks/exhaustive-deps

  // let imgSrc = `${Auth.ecms}/api/redirectToResource?authKey=${Auth.authKey}&resourceFolder=userFiles&resourceFileName=${model.imageSource}`;

  const strokeColor = drawService[lessonElementId].currentStrokeColor;
  const fillColor = drawService[lessonElementId].currentFillColor;

  const width = drawService[lessonElementId].getWidth();
  const height = drawService[lessonElementId].getHeight();

  let widthPx = `${width }px`;
  let heightPx = `${height }px`;

  useEffect(() => {
    setTextResponseWidth(null);
    setTextResponseHeight(TEXT_RESPONSE_MIN_HEIGHT);
  }, [width]);

  const parentModel = model.parentActivityElementId
    ? lessonManager.getSlideModel(model.parentActivityElementId)
    : null;
  const isActivityChild = Boolean(model.isActivityPart || model.parentActivityElementId);
  const isMultipartChild = Boolean(
    isActivityChild && parentModel?.type === ContentType.MULTIPART_ACTIVITY.type
  );
  const textResponseMaxWidth = isMultipartChild
    ? TEXT_RESPONSE_MAX_WIDTH_MULTIPART
    : isActivityChild
      ? TEXT_RESPONSE_MAX_WIDTH_ACTIVITY
      : TEXT_RESPONSE_MAX_WIDTH;

  const clampTextResponseWidth = (nextWidth) => Math.min(
    textResponseMaxWidth,
    Math.max(TEXT_RESPONSE_MIN_WIDTH, Math.round(nextWidth))
  );

  const clampTextResponseHeight = (nextHeight) => Math.min(
    TEXT_RESPONSE_MAX_HEIGHT,
    Math.max(TEXT_RESPONSE_MIN_HEIGHT, Math.round(nextHeight))
  );

  const textResponseEffectiveWidth = clampTextResponseWidth(textResponseWidth ?? width);
  const textResponseEffectiveHeight = clampTextResponseHeight(textResponseHeight ?? TEXT_RESPONSE_MIN_HEIGHT);

  const handleTextResponseResizeKeyDown = (e) => {
    if (readOnly) {
      return;
    }
    const step = e.shiftKey ? TEXT_RESPONSE_RESIZE_STEP_LARGE : TEXT_RESPONSE_RESIZE_STEP;
    let handled = true;

    if (e.key === 'ArrowRight') {
      setTextResponseWidth(clampTextResponseWidth(textResponseEffectiveWidth + step));
    } else if (e.key === 'ArrowLeft') {
      setTextResponseWidth(clampTextResponseWidth(textResponseEffectiveWidth - step));
    } else if (e.key === 'ArrowDown') {
      setTextResponseHeight(clampTextResponseHeight(textResponseEffectiveHeight + step));
    } else if (e.key === 'ArrowUp') {
      setTextResponseHeight(clampTextResponseHeight(textResponseEffectiveHeight - step));
    } else if (e.key === 'Home') {
      setTextResponseWidth(width);
      setTextResponseHeight(TEXT_RESPONSE_MIN_HEIGHT);
    } else if (e.key === 'End') {
      setTextResponseWidth(textResponseMaxWidth);
      setTextResponseHeight(TEXT_RESPONSE_MAX_HEIGHT);
    } else {
      handled = false;
    }

    if (handled) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleTextResponseResizePointerDown = (e) => {
    if (readOnly || e.button !== 0) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = textResponseEffectiveWidth;
    const startHeight = textResponseEffectiveHeight;

    const onPointerMove = (moveEvent) => {
      setTextResponseWidth(clampTextResponseWidth(startWidth + (moveEvent.clientX - startX)));
      setTextResponseHeight(clampTextResponseHeight(startHeight + (moveEvent.clientY - startY)));
    };

    const onPointerUp = () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  const imageTitle = model.imageTitle || '';
  // const imageTitleId = drawService[lessonElementId].getImageTitleId();
  const [altText, setAltText] = useState(model && model.altText ? model.altText : '');

  const canvasId = `${model.id }_canvas`;
  const drawCanvasId = `${model.id }_drawCanvas`;

  let imageSrc = null;
  if (model.croppedImageSource) {
    imageSrc = Auth.getResourceUrlByFileName(model.croppedImageSource);
  } else if (model.imageSource) {
    imageSrc = Auth.getResourceUrlByFileName(model.imageSource);
  }

  if (lessonManager.playerMode === LessonMode.PRINT_PREVIEW) {
    widthPx = (imageSrc) ? '3in' : '1.5in';
    heightPx = (imageSrc) ? '3in' : '1.5in';
  }

  let toolbar = MINIMUM_TOOLBAR;
  if (model.responseFormat === 'html') {
    if (model.toolbarType === 'mathToolbar') {
      toolbar = MATH_TOOLBAR;
    } else if (model.toolbarType === 'advancedToolbar') {
      toolbar = ADVANCED_CKEDITOR_TOOLBAR;
    } else if (model.toolbarType === 'mathAdvancedToolbar') {
      toolbar = ADVANCED_MATH_CKEDITOR_TOOLBAR;
    } else if (model.toolbarType === 'mathWithTableToolbar') {
      toolbar = MATH_WITH_TABLE_TOOLBAR;
    }
  }
  // richText.createEditor(textId, {onClick:function() {
  //     self.disableTextDragging();
  //     richText.getEditor(textId).focus();
  //   },
  //   onBlur:function(e) {
  //     self.updateState();
  //     self.enableTextDragging();
  //   },
  //   toolbar:toolbar
  // });

  const pointOptions = [
    { value: 'closed', label: <span className='pointIcon pointIcon-closed'>&nbsp;</span> },
    { value: 'open', label: <span className='pointIcon pointIcon-open'>&nbsp;</span> },
  ];

  const lineOptions = [
    { value: drawService[lessonElementId].LINE, label: <span className={`lineIcon lineIcon-${drawService[lessonElementId].LINE}`}>&nbsp;</span> },
    { value: `${drawService[lessonElementId].LINE}-dashed`, label: <span className={`lineIcon lineIcon-${drawService[lessonElementId].LINE}-dashed`}>&nbsp;</span> },
    { value: drawService[lessonElementId].RAY, label: <span className={`lineIcon lineIcon-${drawService[lessonElementId].RAY}`}>&nbsp;</span> },
    { value: `${drawService[lessonElementId].RAY}-dashed`, label: <span className={`lineIcon lineIcon-${drawService[lessonElementId].RAY}-dashed`}>&nbsp;</span> },
    { value: drawService[lessonElementId].SEGMENT, label: <span className={`lineIcon lineIcon-${drawService[lessonElementId].SEGMENT}`}>&nbsp;</span> },
    { value: `${drawService[lessonElementId].SEGMENT}-hidden-hidden`, label: <span className={`lineIcon lineIcon-${drawService[lessonElementId].SEGMENT}-hidden-hidden`}>&nbsp;</span> },
    { value: `${drawService[lessonElementId].SEGMENT}-dashed`, label: <span className={`lineIcon lineIcon-${drawService[lessonElementId].SEGMENT}-dashed`}>&nbsp;</span> },
    { value: drawService[lessonElementId].VECTOR, label: <span className={`lineIcon lineIcon-${drawService[lessonElementId].VECTOR}`}>&nbsp;</span> },
    { value: `${drawService[lessonElementId].VECTOR}-open-hidden`, label: <span className={`lineIcon lineIcon-${drawService[lessonElementId].VECTOR}-open-hidden`}>&nbsp;</span> },
    { value: `${drawService[lessonElementId].VECTOR}-dashed`, label: <span className={`lineIcon lineIcon-${drawService[lessonElementId].VECTOR}-dashed`}>&nbsp;</span> },
  ];

  const handleChangeStrokeColor = (color) => {
    drawService[lessonElementId].setStrokeColor(color.hex);
  };

  const handleChangeFillColor = (color) => {
    drawService[lessonElementId].setFillColor(color.hex);
  };

  const handleChangeBrushSize = (data) => {
    drawService[lessonElementId].setStrokeWidth(data.value);
  };

  const handleChangeShape = (data) => {
    drawService[lessonElementId].addShape(data.value);
  };

  const handleChangeLine = (data) => {
    drawService[lessonElementId].addLine(data.value);
  };

  const handleChangePoint = (data) => {
    drawService[lessonElementId].addPoint(data.value);
  };

  const handleSelect = () => {
    drawService[lessonElementId].select();
  };

  const handleCopy = () => {
    drawService[lessonElementId].copy();
  };

  const handleUndo = () => {
    drawService[lessonElementId].undo();
  };

  const handleRedo = () => {
    drawService[lessonElementId].redo();
  };

  const handleDeleteText = (textId, lessonElementId) => {
    drawManager.deleteTextContainer(textId, lessonElementId);
    drawService[lessonElementId].updateState();
    drawService[lessonElementId].updateResponseModel();
  };

  const navToCurrentLessonElement = async () => {
    if (lessonManager.currentLessonElementId !== lessonElementId) {
      responseService.setSolutionDialogOpen(false);
      responseService.setHintDialogOpen(false);
      await navMenuService.navToSlideClickHandler(lessonElementId, { shouldScrollQuestionIntoView: false });
    }
  };

  const handleTextPointerDown = (e, textId) => {
    if (readOnly || e.button !== 0) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    navToCurrentLessonElement();
    const el = document.getElementById(textId);
    const rect = el?.getBoundingClientRect();
    drawManager.dragStartTextContainer(
      textId,
      e.clientX,
      e.clientY,
      rect?.width || 0,
      rect?.height || 0,
      lessonElementId
    );

    const onMove = (moveEvent) => {
      drawManager.moveTextContainer(moveEvent.clientX, moveEvent.clientY, width, height);
    };
    const onUp = (upEvent) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      drawManager.dropTextContainer(upEvent.clientX, upEvent.clientY, lessonElementId, width, height);
      drawService[lessonElementId].updateState();
      drawService[lessonElementId].updateResponseModel();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const textContainers = drawManager.textContainers[lessonElementId];

  // Check if this question has response dependency
  const responseDependencyValue = lessonManager.lessonOptions?.responseDependency?.[lessonElementId];
  let hasResponseDependency = false;
  let isDifferentLessonSource = false;
 
  if (responseDependencyValue) {
    // Handle new format where value is an object
    if (typeof responseDependencyValue === 'object') {
      if (responseDependencyValue.source === 'inLesson') {
        hasResponseDependency = responseDependencyValue.questionId;
      } else if (responseDependencyValue.source === 'differentLesson') {
        isDifferentLessonSource = true;
        hasResponseDependency = responseDependencyValue.lessonId && 
                                responseDependencyValue.questionForeverId &&
                                responseDependencyValue.lessonId !== '' &&
                                responseDependencyValue.questionForeverId !== '';
      }
    } else {
      // Handle old format where value is a string (questionId)
      hasResponseDependency = responseDependencyValue !== null && responseDependencyValue !== '';
    }
  }
  const isSubmitted = lessonElementState ? lessonElementState.isSubmitted : false;

  // Handler for showing previous response
  const handleShowPreviousResponse = async () => {
    if (lessonElementState && lessonElementState.questionBehavior && lessonElementState.questionBehavior.hasResponseDependency(model, lessonElementId)) {
      // Check if this is a differentLesson source
      if (isDifferentLessonSource) {
        // Check if we're in activity mode
        if (PlayerService.isActivityMode()) {
          // Call the API to fetch prior responses
          await lessonElementState.questionBehavior.fetchPriorResponsesFromDifferentLesson(responseDependencyValue, lessonElementId);
        } else {
          // Show dialog for non-activity mode
          setShowPreviewUnavailable(true);
        }
        return;
      }
      
      // Check if user has current content that would be overwritten
      if (lessonElementState.questionBehavior.hasCurrentContent(lessonElementId)) {
        setShowPreviousResponseConfirm(true);
      } else {
        // No current content, proceed directly
        lessonElementState.questionBehavior.populateWithPreviousResponse(lessonElementId, model);
      }
    }
  };

  const handleConfirmShowPreviousResponse = () => {
    setShowPreviousResponseConfirm(false);
    if (lessonElementState && lessonElementState.questionBehavior && lessonElementState.questionBehavior.hasResponseDependency(model, lessonElementId)) {
      lessonElementState.questionBehavior.populateWithPreviousResponse(lessonElementId, model);
    }
  };

  const handleCancelShowPreviousResponse = () => {
    setShowPreviousResponseConfirm(false);
  };

  // Handler for submission with warning
  const handleSubmitWithWarning = async () => {
    if (hasResponseDependency && !isSubmitted) {
      setShowSubmissionWarning(true);
    } else {
      await responseService.submitButtonHandler(null, lessonElementId);
    }
  };

  const handleConfirmSubmission = async () => {
    setShowSubmissionWarning(false);
    await responseService.submitButtonHandler(null, lessonElementId);
  };

  const handleCancelSubmission = () => {
    setShowSubmissionWarning(false);
  };

  const svc = drawService[lessonElementId];

  return (
    <div className='draw-wrapper-container' ref={drawAnchorRef}>
      <div className='testItemQuestion'>
        {(lessonManager.playerMode === LessonMode.PRINT_PREVIEW) && <PrintQuestionNumber model={model} />}
        <HtmlComponent htmlStr={model.questionText} />
      </div>
      <HtmlComponent className='image-title' htmlStr={model.imageTitle} />

      {/* Show Previous Response Button */}
      {hasResponseDependency && lessonManager.playerMode !== LessonMode.SCORING && lessonManager.playerMode !== LessonMode.REVIEW && (
        <div className='response-dependency-controls'>
          <ShowPreviousResponseButton
            lessonElementId={lessonElementId}
            onShowPreviousResponse={handleShowPreviousResponse}
            isDisabled={isSubmitted}
          />
        </div>
      )}

      <>
        <div
          id={`draw-float-stage-${lessonElementId}`}
          className={`draw-float-stage${isActivityChild && !isMultipartChild ? ' is-activity-child' : ''}`}
          data-draw-float-lesson={lessonElementId}
          style={{ width: widthPx }}
        >
          {!readOnly && (
            <DrawFloatingToolbar
              lessonElementId={lessonElementId}
              drawWidth={widthPx}
              stickyScroll={UtilsService.isNullOrEmpty(model.parentActivityElementId)}
              allowHorizontal={isActivityChild && !isMultipartChild}
              readOnly={readOnly}
              hasGraph={model.hasGraph}
              strokeColor={strokeColor}
              fillColor={fillColor}
              shapeConstants={{
                TRIANGLE: svc.TRIANGLE,
                SQUARE: svc.SQUARE,
                CIRCLE: svc.CIRCLE,
                STAR: svc.STAR,
              }}
              lineOptions={lineOptions}
              pointOptions={pointOptions}
              onSelect={handleSelect}
              onPencil={() => svc.onClickPencilTool()}
              onBrush={() => svc.onClickPaintTool()}
              onText={() => svc.onClickTextTool()}
              onCopy={handleCopy}
              onUndo={handleUndo}
              onRedo={handleRedo}
              onDelete={() => svc.deleteFromDraw()}
              onStrokeColorChange={handleChangeStrokeColor}
              onFillColorChange={handleChangeFillColor}
              onBrushSizeChange={handleChangeBrushSize}
              onShapeTool={() => svc.onClickShapeTool()}
              onShapeChange={handleChangeShape}
              onLineChange={handleChangeLine}
              onPointChange={handleChangePoint}
            />
          )}
          <div id={`${model.id}-draw-wrapper`} ref={drawDrawableRef} className='draw-wrapper' style={{ position: 'relative', height: heightPx, width: widthPx }}>
          <div className='drawBody imageBody ' data-allow='1' style={{ width: widthPx, height: heightPx }}>
            {imageSrc && (
              <img alt={altText} className='imageLabelImage' onLoad={imageLoaded} src={imageSrc}
                style={{ maxHeight: heightPx }} tabIndex={0} />
            )}
          </div>
          {(lessonManager.playerMode === LessonMode.PRINT_PREVIEW) && (
            <div className='canvasContainerOuter' id='canvasContainerOuter'
              style={{ height: heightPx, width: widthPx }} />
          )}
          {(lessonManager.playerMode !== LessonMode.PRINT_PREVIEW) && (
            <div className='canvasContainerOuter' id='canvasContainerOuter'
              style={{ height: heightPx, width: widthPx }}>

              {(textContainers || []).map((textContainer) => {
                const isEmpty = !UtilsService.stripTagsAndEntities(textContainer.text || '').trim();
                return (
                  <div key={textContainer.textId} className={`drawTextContainer deleteButtonParent${isEmpty && !readOnly ? ' is-empty' : ''}`} id={textContainer.textId}
                    style={{ top: textContainer.top, left: textContainer.left, opacity: textContainer.opacity }}>
                    {!readOnly && (
                      <div
                        className='drawTextContainerHandle'
                        onPointerDown={(e) => handleTextPointerDown(e, textContainer.textId)}
                        role='button'
                        tabIndex={0}
                        aria-label={t('moveText', 'Move text')}
                      />
                    )}
                    <div className='deleteButton deleteTextContainerButton'
                      onClick={() => !readOnly && handleDeleteText(textContainer.textId, lessonElementId)}
                      style={{ opacity: readOnly ? 0.5 : 1, cursor: readOnly ? 'not-allowed' : 'pointer' }} />
                    <div className='drawText oneRowToolbar cke_editable cke_editable_inline cke_contents_ltr cke_show_borders placeholder' id={`${textContainer.textId}_text`}>

                      <RichTextEditor
                        data={(textContainer.text) ? textContainer.text : ''}
                        lessonElementId={lessonElementId}
                        onBlur={() => {
                          if (!readOnly) {
                            drawService[lessonElementId].updateState();
                            drawService[lessonElementId].updateResponseModel();
                          }
                        }}
                        onChange={(data) => {
                          if (!readOnly) {
                            drawManager.setTextContainerText(textContainer.textId, data, lessonElementId);
                          }
                        }}
                        placeHolder=''
                        readOnly={readOnly}
                        toolbar={toolbar} />
                    </div>
                  </div>
                );
              })}

              <div className='body canvasOrganizerBody  canvasContainer' id={`${model.id}_canvasContainer`} style={{ height: heightPx, width: widthPx }}>
                <canvas className='canvas' id={canvasId} />
              </div>

              <div className='body canvasOrganizerBody  drawCanvasContainer' id={`${model.id}_drawCanvasContainer`} style={{ width: widthPx, height: heightPx }}>
                <canvas className='canvas' id={drawCanvasId} />
              </div>

              {/* Invisible overlay to prevent all interactions when read-only */}
              {readOnly && (
                <div
                  className='draw-readonly-overlay'
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: 10,
                    pointerEvents: 'all',
                    backgroundColor: 'transparent'
                  }}
                />
              )}

            </div>
          )}
          </div>
          {!readOnly && (
            <div
              id={`draw-float-anchor-bottom-${lessonElementId}`}
              className='draw-float-anchor draw-float-anchor-bottom'
              data-draw-float-lesson={lessonElementId}
              aria-hidden='true'
            />
          )}
          {model.textAnswer && lessonElementState && (
            <div className='draw-text-response-wrapper'>
              <div className='draw-text-response-label'>{t('textResponseOptional')}</div>
              <div
                className={`draw-text-response${isMultipartChild ? ' is-multipart-child' : isActivityChild ? ' is-activity-child' : ''}`}
                style={{
                  width: `${textResponseEffectiveWidth}px`,
                  height: lessonManager.playerMode === LessonMode.PRINT_PREVIEW
                    ? undefined
                    : `${textResponseEffectiveHeight}px`,
                  minWidth: `${TEXT_RESPONSE_MIN_WIDTH}px`,
                  maxWidth: `${textResponseMaxWidth}px`
                }}
              >
                {(lessonManager.playerMode === LessonMode.PRINT_PREVIEW) ? (
                  <div className='draw-text-response-print'>
                    <HtmlComponent htmlStr={lessonElementState.currentResponseAnswer?.textAnswer || ''} />
                  </div>
                ) : (
                  <div className='cke_topspace' aria-label={t('textResponseOptional')}>
                    <RichTextEditor
                      data={lessonElementState.currentResponseAnswer?.textAnswer || ''}
                      disabled={readOnly}
                      lessonElementId={lessonElementId}
                      onBlur={() => styleService.resetStyleVar('--ck-z-modal')}
                      onChange={(data) => {
                        if (!readOnly) {
                          responseService.textChangeHandler(data, lessonElementId);
                        }
                      }}
                      onFocus={() => styleService.setStyleVar('--ck-z-modal', '102')}
                      placeHolder={t('enterHere')}
                      toolbar={toolbar}
                    />
                  </div>
                )}
                {!readOnly && lessonManager.playerMode !== LessonMode.PRINT_PREVIEW && (
                  <>
                    <button
                      type='button'
                      className='draw-text-response-resize-handle'
                      aria-label={t('textResponseResizeHandle')}
                      aria-describedby={`draw-text-response-resize-help-${lessonElementId}`}
                      aria-valuetext={t('textResponseSize', {
                        width: textResponseEffectiveWidth,
                        height: textResponseEffectiveHeight
                      })}
                      onKeyDown={handleTextResponseResizeKeyDown}
                      onPointerDown={handleTextResponseResizePointerDown}
                    />
                    <span
                      id={`draw-text-response-resize-help-${lessonElementId}`}
                      className='sr-only'
                    >
                      {t('textResponseResizeInstructions')}
                    </span>
                    <span className='sr-only' aria-live='polite'>
                      {t('textResponseSize', {
                        width: textResponseEffectiveWidth,
                        height: textResponseEffectiveHeight
                      })}
                    </span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </>
      {((lessonManager.playerMode === LessonMode.PRINT_PREVIEW) && lessonManager.isForceTeacher) && (
        <div className='print-teacher-notes'>
          <HtmlComponent htmlStr={model.validation.solution} />
        </div>
      )}

      {/* Submission Warning Dialog */}
      <SubmissionWarningDialog
        isOpen={showSubmissionWarning}
        onConfirm={handleConfirmSubmission}
        onCancel={handleCancelSubmission}
        message={t('submissionWarningMessage')}
      />

      {/* Show Previous Response Confirmation Dialog */}
      <ShowPreviousResponseConfirmDialog
        isOpen={showPreviousResponseConfirm}
        onConfirm={handleConfirmShowPreviousResponse}
        onCancel={handleCancelShowPreviousResponse}
        message={t('showPreviousResponseWarning')}
      />
      
      {/* Preview Unavailable Dialog */}
      <PreviewUnavailableDialog
        isOpen={showPreviewUnavailable}
        onClose={() => setShowPreviewUnavailable(false)}
        message="This feature is only available during live question answering sessions. It cannot be previewed in this mode."
      />

    </div>
  );
});

export default Draw;
